import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { useTenant } from "../../contexts/TenantContext";
import { BrandButton, inputCls, labelCls } from "./TenantShell";
import { tapi, apiErr } from "../../lib/tenant";
import { DESTINATION_COUNTRIES } from "../../lib/destinationCountries";
import { loadPrequal } from "../../lib/lpoPrequal";
import { KesInput } from "./KesInput";

const Money = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className={labelCls}>{label}</span>
    <KesInput required value={value} onChange={onChange} className={inputCls} testid={testid} />
  </label>
);

const YesNo = ({ label, value, onChange, testid }) => (
  <div>
    <span className={labelCls}>{label}</span>
    <div className="flex gap-2" data-testid={testid}>
      {[["yes", true], ["no", false]].map(([k, v]) => (
        <button key={k} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${k}`}
          style={value === v ? { background: "var(--tenant)", borderColor: "var(--tenant)" } : undefined}
          className={"rounded-full border px-4 py-1.5 text-xs font-bold capitalize " + (value === v ? "text-white" : "border-neutral-300 bg-white text-neutral-600")}>{k}</button>
      ))}
    </div>
  </div>
);

const Num = ({ label, value, onChange, testid, prefix, min = 0, step = 1, placeholder, hint }) => (
  <label className="block">
    <span className={labelCls}>{label}</span>
    <div className="relative">
      {prefix && <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-neutral-500">{prefix}</span>}
      <input required type="number" inputMode="numeric" min={min} step={step} value={value} onChange={onChange} placeholder={placeholder}
        className={`${inputCls} ${prefix ? "pl-12" : ""}`} data-testid={testid} />
    </div>
    {hint && <p className="mt-1 text-[11px] text-neutral-400">{hint}</p>}
  </label>
);

// Prefill from the pre-qualification answers where they overlap.
const fromPrequal = (sub) => {
  const pq = loadPrequal(sub)?.answers || {};
  const years = pq.registered_on ? Math.max(0, Math.floor((Date.now() - new Date(pq.registered_on).getTime()) / (365.25 * 864e5))) : "";
  return { years_operational: years === "" ? "" : String(years), funding_required_kes: pq.lpo_value || "",
    supplied_others: pq.supplied_other_client === "yes" ? true : pq.supplied_other_client === "no" ? false : null,
    times_supplied_client: pq.supplied_same_client === "no" ? "0" : "" };
};

export const StepLpoApplication = ({ initial, onSaved }) => {
  const { sub, config } = useTenant();
  const [f, setF] = useState(() => ({
    company_name: "", years_operational: "", lpo_client: "", item_supplied: "", times_supplied_client: "",
    supplied_others: null, times_supplied_others: "", funding_required_kes: "", destination_country: "Kenya",
    existing_debt: null, debt_quantum_kes: "",
    ...fromPrequal(sub), ...(initial?.lpo || {}),
  }));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setV = (k) => (v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (f.supplied_others === null) return toast.error("Tell us whether you have supplied other clients");
    if (f.existing_debt === null) return toast.error("Tell us whether you have existing loans or debt");
    setBusy(true);
    try {
      const body = {
        ...f, years_operational: Number(f.years_operational), times_supplied_client: Number(f.times_supplied_client),
        times_supplied_others: f.supplied_others ? Number(f.times_supplied_others || 0) : null,
        funding_required_kes: Number(f.funding_required_kes),
        debt_quantum_kes: f.existing_debt ? Number(f.debt_quantum_kes || 0) : null,
      };
      await tapi.put(`/t/${sub}/lpo-application`, body);
      toast.success("Application details saved");
      onSaved();
    } catch (err) { toast.error(apiErr(err, "Couldn't save your details")); }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="space-y-5 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm" data-testid="tenant-step-lpo">
      <div><h2 className="text-lg font-extrabold">Tell us about your LPO</h2><p className="text-sm text-neutral-500">A few details about your business and the order you need funded. You can upload the LPO and your records in the next step.</p></div>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block"><span className={labelCls}>Company name</span><input required value={f.company_name} onChange={set("company_name")} className={inputCls} data-testid="lpo-company" /></label>
        <Num label="Years operational" value={f.years_operational} onChange={set("years_operational")} step={0.5} testid="lpo-years" />
        <label className="block"><span className={labelCls}>Name of the client issuing the LPO</span><input required value={f.lpo_client} onChange={set("lpo_client")} className={inputCls} placeholder="e.g. Naivas Limited" data-testid="lpo-client" /></label>
        <label className="block"><span className={labelCls}>Item or service being supplied</span><input required value={f.item_supplied} onChange={set("item_supplied")} className={inputCls} placeholder="e.g. 2,000 bags of maize flour" data-testid="lpo-item" /></label>
        <Num label="How many times have you supplied this specific client with the same goods or services?" value={f.times_supplied_client} onChange={set("times_supplied_client")} testid="lpo-times-client" hint="Enter 0 if this is the first time" />
        <div className="space-y-3">
          <YesNo label="Have you supplied other clients with the same goods or services?" value={f.supplied_others} onChange={setV("supplied_others")} testid="lpo-supplied-others" />
          {f.supplied_others === true && <Num label="Roughly how many times?" value={f.times_supplied_others} onChange={set("times_supplied_others")} testid="lpo-times-others" />}
        </div>
        <Money label="How much funding is required?" value={f.funding_required_kes} onChange={setV("funding_required_kes")} testid="lpo-funding" />
        <label className="block"><span className={labelCls}>Where are the goods destined to go?</span>
          <select required value={f.destination_country} onChange={set("destination_country")} className={inputCls} data-testid="lpo-destination">
            {DESTINATION_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select></label>
        <div className="space-y-3 sm:col-span-2">
          <YesNo label="Do you have any existing loans or active debt obligations?" value={f.existing_debt} onChange={setV("existing_debt")} testid="lpo-existing-debt" />
          {f.existing_debt === true && <div className="sm:max-w-sm"><Money label="What is the total quantum of your current debt obligations?" value={f.debt_quantum_kes} onChange={setV("debt_quantum_kes")} testid="lpo-debt-quantum" /></div>}
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 pt-2">
        <p className="text-xs text-neutral-500">Shared only with {config.name} once you submit.</p>
        <BrandButton type="submit" disabled={busy} data-testid="lpo-submit">{busy ? <Loader2 size={16} className="animate-spin" /> : <>Continue to documents <ArrowRight size={16} /></>}</BrandButton>
      </div>
    </form>
  );
};
